import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, X, Instagram, Youtube, Facebook, ExternalLink, Pencil } from "lucide-react";

const PLATFORM_OPTIONS = [
  { value: "instagram", label: "Instagram reel", icon: Instagram, color: "#DD2A7B" },
  { value: "youtube", label: "YouTube video", icon: Youtube, color: "#FF0000" },
  { value: "facebook", label: "Facebook video", icon: Facebook, color: "#1877F2" },
];

const EMPTY = { platform: "instagram", url: "", title: "", order: 0 };

function LinkForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const editing = !!initial?.id;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const { data } = editing
        ? await api.put(`/admin/media-links/${initial.id}`, form)
        : await api.post(`/admin/media-links`, form);
      onSaved(data);
      onClose();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-xl rounded-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--si-border)]">
          <h2 className="font-display text-2xl">{editing ? "Edit link" : "Add featured video"}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={onSubmit} className="px-6 py-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Platform</label>
            <select className="input-field" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="ml-platform">
              {PLATFORM_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">URL *</label>
            <input required className="input-field" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder={form.platform === "instagram" ? "https://www.instagram.com/reel/XXXX/" : form.platform === "youtube" ? "https://www.youtube.com/watch?v=XXXX" : "https://www.facebook.com/.../videos/..."}
              data-testid="ml-url" />
            <p className="text-xs text-[var(--si-text-2)] mt-2">
              Paste the full URL of the video/reel from the platform.
            </p>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Title / caption (optional)</label>
            <input className="input-field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. The making of a Black Maire flute" data-testid="ml-title" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Display order (lower numbers appear first)</label>
            <input type="number" className="input-field" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} data-testid="ml-order" />
          </div>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm py-2 px-5">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5" data-testid="ml-save">
              {saving ? "Saving..." : editing ? "Save changes" : "Add video"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminMediaLinks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/media-links"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = (saved) => {
    setItems((arr) => {
      const idx = arr.findIndex((i) => i.id === saved.id);
      if (idx >= 0) { const copy = [...arr]; copy[idx] = saved; return copy; }
      return [...arr, saved];
    });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this link?")) return;
    await api.delete(`/admin/media-links/${id}`);
    setItems((arr) => arr.filter((i) => i.id !== id));
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow mb-2">Studio · Connect Page</p>
          <h1 className="font-display text-4xl">Featured videos &amp; reels</h1>
          <p className="text-sm text-[var(--si-text-2)] mt-1">
            Add as many Instagram reels, YouTube videos or Facebook videos as you want — they all appear on the <strong>Connect</strong> page in the order you set.
          </p>
        </div>
        <button className="btn-primary text-sm py-2 px-5" onClick={() => setEditing(EMPTY)} data-testid="add-media-btn">
          <Plus size={16} /> Add video
        </button>
      </div>

      {loading ? (
        <p className="text-[var(--si-text-2)]">Loading...</p>
      ) : items.length === 0 ? (
        <div className="admin-card p-12 text-center">
          <p className="text-[var(--si-text-2)]">No featured videos yet. Add your first Instagram reel or YouTube video.</p>
        </div>
      ) : (
        <div className="admin-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--si-bg-2)]/60 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Platform</th>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">URL</th>
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const p = PLATFORM_OPTIONS.find((x) => x.value === it.platform) || PLATFORM_OPTIONS[0];
                const Icon = p.icon;
                return (
                  <tr key={it.id} className="border-t border-[var(--si-border)]" data-testid={`media-row-${it.id}`}>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-2"><Icon size={16} style={{ color: p.color }} /> {p.label}</span></td>
                    <td className="px-4 py-3">{it.title || <span className="text-[var(--si-text-2)] italic">no title</span>}</td>
                    <td className="px-4 py-3"><a href={it.url} target="_blank" rel="noreferrer" className="text-[var(--si-terracotta)] hover:underline truncate inline-flex items-center gap-1 max-w-[280px]"><span className="truncate">{it.url}</span> <ExternalLink size={12} /></a></td>
                    <td className="px-4 py-3">{it.order}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(it)} className="p-2 hover:text-[var(--si-terracotta)]"><Pencil size={15} /></button>
                      <button onClick={() => onDelete(it.id)} className="p-2 hover:text-red-700"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && <LinkForm initial={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
